/**
 * FlashcardLearning.jsx  —  NISH FSLP
 * ─────────────────────────────────────────────────────────────────────────────
 * Probes active in all three modes (Flip, Quiz, Match).
 * Five probe types cycle per card index: identify → name → attribute → category → function.
 * One probe per card per turn. Same 3-button rating row as existing quality buttons.
 * Backend: `signals: [{ probe, level }]` added to PATCH /flashcard-sessions/:id/review.
 * No schema changes to cards themselves — probes derive from existing `labels`
 * and `category` fields already on every card.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";

const API = import.meta.env.VITE_API_URL || "http://localhost:5001/api";

const LANGUAGES = [
  { code: "en", name: "English",  voice: "en-IN" },
  { code: "ml", name: "മലയാളം", voice: "ml-IN" },
  { code: "hi", name: "हिंदी",   voice: "hi-IN" },
  { code: "ta", name: "தமிழ்",  voice: "ta-IN" },
  { code: "te", name: "తెలుగు", voice: "te-IN" },
];

const MODES = [
  { id: "flip",  label: "Flip Cards",      icon: "🃏", desc: "Tap card → reveal name + audio" },
  { id: "quiz",  label: "Multiple Choice",  icon: "🎯", desc: "Pick the correct card from 4"   },
  { id: "match", label: "Match Pairs",      icon: "🧩", desc: "Match image tiles to label tiles" },
];

// ─── Five probe types ─────────────────────────────────────────────────────────
// Cycle by card index (index % 5). question() receives the card label.
// No per-card setup needed — all five work on existing label + category fields.
const PROBES = [
  {
    id: "identify",
    title: "Identify",
    question: (label) => `Can they identify "${label || "this"}"?`,
    hint: "Point or tap when named — receptive identification",
  },
  {
    id: "name",
    title: "Name it",
    question: (label) => `Can they name it aloud? (expected: "${label || "the word"}")`,
    hint: "Spontaneous expressive naming — verbal, sign, or AAC",
  },
  {
    id: "attribute",
    title: "Attribute",
    question: () => "Can they describe one attribute?",
    hint: "Color, size, shape, or texture",
  },
  {
    id: "category",
    title: "Category",
    question: (label, category) =>
      `What category does ${label || "this"} belong to?` +
      (category && category !== "Custom" ? ` (expected: ${category})` : ""),
    hint: "Fruit, animal, tool, vehicle… concept grouping",
  },
  {
    id: "function",
    title: "Function",
    question: (label) => `What is ${label || "this"} used for?`,
    hint: "Purpose or action — functional understanding",
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────
async function apiFetch(method, path, body, headers) {
  const h = { "Content-Type": "application/json", ...(headers || {}) };
  const res = await fetch(`${API}${path}`, {
    method, headers: h,
    body: body ? JSON.stringify(body) : undefined,
  });
  const t = await res.text();
  return t ? JSON.parse(t) : {};
}

async function closeSession(sessionId, getAuthHeader, startMs) {
  if (!sessionId) return;
  try {
    await apiFetch(
      "PUT",
      `/flashcard-sessions/${sessionId}/close`,
      { durationMin: Math.round((Date.now() - (startMs || Date.now())) / 60000) },
      getAuthHeader()
    );
  } catch {}
}

function speak(text, langCode) {
  if (!window.speechSynthesis || !text) return;
  window.speechSynthesis.cancel();
  const lang = LANGUAGES.find(l => l.code === langCode) || LANGUAGES[0];
  const utt  = new SpeechSynthesisUtterance(text);
  utt.lang   = lang.voice;
  utt.rate   = 0.85;
  utt.pitch  = 1.1;
  window.speechSynthesis.speak(utt);
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function Spinner() {
  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center",
                  padding:60, flexDirection:"column", gap:12 }}>
      <div style={{ width:28, height:28, border:"3px solid #e0e7f0",
                    borderTop:"3px solid #003366", borderRadius:"50%",
                    animation:"spin .7s linear infinite" }} />
      <div style={{ fontSize:13, color:"#aaa" }}>Loading…</div>
    </div>
  );
}

// ─── Star burst reward ────────────────────────────────────────────────────────
function StarBurst({ count = 3 }) {
  return (
    <div style={{ position:"fixed", inset:0, pointerEvents:"none", zIndex:900, overflow:"hidden" }}>
      {Array.from({ length: count * 8 }).map((_, i) => {
        const x     = Math.random() * 100;
        const dur   = 0.8 + Math.random() * 0.6;
        const delay = Math.random() * 0.4;
        const color = ["#FFD700","#FF6B35","#1B6B45","#003366","#7B2D8B","#E65100"][i % 6];
        return (
          <div key={i} style={{
            position:"absolute", left:`${x}%`, top:"60%",
            width:10, height:10, borderRadius:"50%", background:color,
            animation:`burst${i%2===0?"Up":"Diag"} ${dur}s ease-out ${delay}s forwards`,
            opacity:0,
          }} />
        );
      })}
      <style>{`
        @keyframes burstUp   { 0%{opacity:1;transform:translateY(0) scale(1)} 100%{opacity:0;transform:translateY(-300px) scale(.4)} }
        @keyframes burstDiag { 0%{opacity:1;transform:translate(0,0) scale(1)} 100%{opacity:0;transform:translate(${Math.random()>0.5?120:-120}px,-260px) scale(.3)} }
      `}</style>
    </div>
  );
}

// ─── Celebration screen ───────────────────────────────────────────────────────
function CelebrationScreen({ correct, total, childName, onReplay, onExit }) {
  const pct   = total > 0 ? Math.round((correct / total) * 100) : 0;
  const stars = pct >= 85 ? 3 : pct >= 60 ? 2 : 1;
  const msgs  = [
    "Keep practising — you'll get there! 💪",
    "Good effort! Try again to improve 🌟",
    "Great job! Well done 🎉",
  ];
  return (
    <div style={{ minHeight:"100vh",
                  background:"linear-gradient(135deg,#003366 0%,#1a3a6e 60%,#2d1b5e 100%)",
                  display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
      <StarBurst count={stars} />
      <div style={{ background:"white", borderRadius:24, padding:"40px 32px", maxWidth:440, width:"100%",
                    textAlign:"center", boxShadow:"0 24px 80px rgba(0,0,0,.4)",
                    position:"relative", zIndex:10 }}>
        <div style={{ fontSize:48, marginBottom:8, letterSpacing:4 }}>
          {Array.from({ length: 3 }).map((_, i) => (
            <span key={i} style={{ opacity: i < stars ? 1 : 0.2 }}>⭐</span>
          ))}
        </div>
        <div style={{ fontFamily:"'Playfair Display',serif", fontSize:26, fontWeight:900,
                      color:"#003366", marginBottom:4 }}>
          {stars === 3 ? "Amazing!" : stars === 2 ? "Well done!" : "Good try!"}
        </div>
        {childName && <div style={{ fontSize:14, color:"#888", marginBottom:20 }}>{childName}</div>}
        <div style={{ fontSize:13, color:"#555", marginBottom:28, lineHeight:1.6 }}>
          {msgs[stars - 1]}
        </div>
        <div style={{ display:"flex", justifyContent:"center", gap:20, marginBottom:28 }}>
          {[
            { label:"Score",   val:`${pct}%`, color:pct>=85?"#1B6B45":pct>=60?"#D4AC0D":"#C0392B" },
            { label:"Correct", val:correct,   color:"#1B6B45" },
            { label:"Total",   val:total,     color:"#003366" },
          ].map(s => (
            <div key={s.label} style={{ textAlign:"center" }}>
              <div style={{ fontSize:28, fontWeight:900, color:s.color }}>{s.val}</div>
              <div style={{ fontSize:10, color:"#aaa", fontWeight:600,
                            textTransform:"uppercase" }}>{s.label}</div>
            </div>
          ))}
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {onReplay && (
            <button onClick={onReplay}
              style={{ padding:"13px", background:"#003366", color:"white", border:"none",
                       borderRadius:12, fontWeight:700, fontSize:14, cursor:"pointer",
                       boxShadow:"0 4px 16px rgba(0,51,102,.3)" }}>
              🔄 Play Again
            </button>
          )}
          <button onClick={onExit}
            style={{ padding:"13px", background:"white", color:"#666",
                     border:"1.5px solid #dce3f5", borderRadius:12,
                     fontWeight:700, fontSize:14, cursor:"pointer" }}>
            ← Back to Setup
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── SessionNotesScreen ───────────────────────────────────────────────────────
function SessionNotesScreen({ sessionId, getAuthHeader, startMs, onDone, childName, score }) {
  const [notes,  setNotes]  = useState("");
  const [gen,    setGen]    = useState("");
  const [saving, setSaving] = useState(false);

  const scoreColor = score >= 85 ? "#1B6B45" : score >= 60 ? "#D4AC0D" : "#C0392B";
  const scoreBg    = score >= 85 ? "#EDF7F2" : score >= 60 ? "#FEF9E7" : "#FDEDEC";

  async function save() {
    setSaving(true);
    try {
      await apiFetch("PUT", `/flashcard-sessions/${sessionId}/close`, {
        durationMin:        Math.round((Date.now() - (startMs || Date.now())) / 60000),
        sessionNotes:       notes,
        generalizationNote: gen,
      }, getAuthHeader());
    } catch {}
    setSaving(false);
    onDone();
  }

  return (
    <div style={{ minHeight:"100vh",
                  background:"linear-gradient(135deg,#003366 0%,#1a3a6e 60%,#2d1b5e 100%)",
                  display:"flex", alignItems:"center", justifyContent:"center", padding:24,
                  fontFamily:"'Segoe UI',Arial,sans-serif" }}>
      <div style={{ background:"white", borderRadius:22, padding:"34px 30px", width:500,
                    maxWidth:"96vw", boxShadow:"0 24px 80px rgba(0,0,0,.4)" }}>
        <div style={{ textAlign:"center", marginBottom:26 }}>
          <div style={{ fontSize:40, marginBottom:10 }}>📝</div>
          <div style={{ fontFamily:"'Playfair Display',serif", fontSize:21, fontWeight:900,
                        color:"#1a1a2e", marginBottom:8 }}>Session Notes</div>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:10 }}>
            {childName && <span style={{ fontSize:13, color:"#666", fontWeight:600 }}>{childName}</span>}
            {childName && <span style={{ color:"#ddd" }}>·</span>}
            <span style={{ fontSize:13, fontWeight:800, color:scoreColor, background:scoreBg,
                           padding:"2px 12px", borderRadius:20,
                           border:`1px solid ${scoreColor}40` }}>
              {score}% score
            </span>
          </div>
        </div>
        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:10, fontWeight:700, color:"#888", textTransform:"uppercase",
                        letterSpacing:.8, marginBottom:6 }}>Session Observations</div>
          <textarea autoFocus value={notes} onChange={e => setNotes(e.target.value)} rows={3}
            placeholder="Attention span, engagement, behaviour, fatigue, prompts needed…"
            style={{ width:"100%", border:"1.5px solid #dce3f5", borderRadius:10,
                     padding:"10px 13px", fontSize:14, outline:"none", resize:"vertical",
                     boxSizing:"border-box", fontFamily:"inherit", lineHeight:1.6 }}
            onFocus={e => e.target.style.borderColor = "#003366"}
            onBlur={e  => e.target.style.borderColor = "#dce3f5"} />
        </div>
        <div style={{ marginBottom:26 }}>
          <div style={{ fontSize:10, fontWeight:700, color:"#888", textTransform:"uppercase",
                        letterSpacing:.8, marginBottom:6 }}>
            Generalization Note{" "}
            <span style={{ fontWeight:400, textTransform:"none", letterSpacing:0 }}>(optional)</span>
          </div>
          <textarea value={gen} onChange={e => setGen(e.target.value)} rows={2}
            placeholder="Did the child apply any learned concepts outside of this session?"
            style={{ width:"100%", border:"1.5px solid #dce3f5", borderRadius:10,
                     padding:"10px 13px", fontSize:14, outline:"none", resize:"vertical",
                     boxSizing:"border-box", fontFamily:"inherit", lineHeight:1.6 }}
            onFocus={e => e.target.style.borderColor = "#003366"}
            onBlur={e  => e.target.style.borderColor = "#dce3f5"} />
        </div>
        <div style={{ display:"flex", gap:10 }}>
          <button onClick={onDone}
            style={{ flex:1, padding:"12px", background:"white", border:"1.5px solid #dce3f5",
                     borderRadius:11, color:"#666", fontWeight:700, fontSize:14, cursor:"pointer" }}>
            Skip
          </button>
          <button onClick={save} disabled={saving}
            style={{ flex:2, padding:"12px",
                     background:saving ? "#aaa" : "#003366",
                     color:"white", border:"none", borderRadius:11, fontWeight:700, fontSize:14,
                     cursor:saving ? "default" : "pointer",
                     boxShadow:saving ? "none" : "0 4px 16px rgba(0,51,102,.3)" }}>
            {saving ? "Saving…" : "✓ Save & Exit"}
          </button>
        </div>
        <div style={{ textAlign:"center", marginTop:12, fontSize:11, color:"#aaa" }}>
          Notes are saved to the child's session log in Progress Tracker
        </div>
      </div>
    </div>
  );
}

// ─── ProbeRound ───────────────────────────────────────────────────────────────
// Shared by all three modes. One probe per card per turn.
// probe     = PROBES[cardIndex % PROBES.length]
// label     = card label in current language
// category  = card.category (used by the "category" probe)
// onRate(level) = called with "absent" | "emerging" | "strong"
// submitting    = disables buttons while posting
function ProbeRound({ probe, label, category, onRate, submitting }) {
  return (
    <div style={{ marginTop:20, width:"min(440px,90vw)" }}>
      <div style={{ background:"#EBF4FB", borderRadius:12, padding:"13px 16px",
                    border:"1.5px solid #BDD7EE", marginBottom:14 }}>
        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:5 }}>
          <span style={{ fontSize:10, fontWeight:800, color:"#1A4A6E", letterSpacing:.8,
                         textTransform:"uppercase", background:"#BDD7EE",
                         padding:"2px 8px", borderRadius:6 }}>
            {probe.title}
          </span>
        </div>
        <div style={{ fontWeight:700, fontSize:14, color:"#003366", lineHeight:1.5, marginBottom:4 }}>
          {probe.question(label, category)}
        </div>
        <div style={{ fontSize:11, color:"#5C7A9E", fontStyle:"italic" }}>
          {probe.hint}
        </div>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10 }}>
        {[
          { level:"absent",   label:"Needs Help",  emoji:"🔴", color:"#C0392B", bg:"#FDEDEC" },
          { level:"emerging", label:"With Prompt", emoji:"🟡", color:"#D4AC0D", bg:"#FEF9E7" },
          { level:"strong",   label:"Got It! ✓",   emoji:"🟢", color:"#1B6B45", bg:"#EDF7F2" },
        ].map(r => (
          <button key={r.level} onClick={() => onRate(r.level)} disabled={submitting}
            style={{ padding:"16px 8px", background:r.bg, border:`2.5px solid ${r.color}`,
                     borderRadius:16, cursor:"pointer", fontWeight:800, fontSize:13,
                     color:r.color, display:"flex", flexDirection:"column",
                     alignItems:"center", gap:6, opacity:submitting ? 0.5 : 1, minHeight:70 }}>
            <span style={{ fontSize:26 }}>{r.emoji}</span>
            {r.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MODE 1 — FLIP CARDS  (with probe after every quality rating)
// ══════════════════════════════════════════════════════════════════════════════
//
// Flow per card:
//   1. Tap card → reveal label + audio
//   2. Quality rating → stores quality, moves to 'probe' phase
//   3. Probe question shown (cycles through 5 by card index)
//      → both ratings posted together with signals[], card advances
//
function FlipMode({ cards, language, sessionId, getAuthHeader, childName, onDone, readOnly }) {
  const [index,          setIndex]          = useState(0);
  const [flipped,        setFlipped]        = useState(false);
  const [history,        setHistory]        = useState([]);
  const [submitting,     setSubmitting]     = useState(false);
  const [showNotes,      setShowNotes]      = useState(false);
  const [phase,          setPhase]          = useState("rate"); // "rate" | "probe"
  const [pendingQuality, setPendingQuality] = useState(null);

  const startMs      = useRef(Date.now());
  const sessionStart = useRef(Date.now());
  const closedRef    = useRef(false);

  const card  = cards[index];
  const label = card?.labels?.[language] || card?.labels?.en || card?.cardLabel || "";
  const done  = index >= cards.length;
  const probe = PROBES[index % PROBES.length];

  useEffect(() => {
    if (done && !closedRef.current) {
      closedRef.current = true;
      closeSession(sessionId, getAuthHeader, sessionStart.current);
    }
  }, [done]);

  const revealCard = () => {
    if (flipped) return;
    setFlipped(true);
    speak(label, language);
  };

  const handleQualityRate = (quality) => {
    if (submitting) return;
    setPendingQuality(quality);
    setPhase("probe");
  };

  const handleProbeRate = async (probeLevel) => {
    if (submitting || pendingQuality === null) return;
    setSubmitting(true);
    const responseMs = Date.now() - startMs.current;
    try {
      if (sessionId) {
        await apiFetch("PATCH", `/flashcard-sessions/${sessionId}/review`, {
          cardId:   card.cardId || card.id,
          quality:  pendingQuality,
          responseMs,
          prompted: pendingQuality < 3,
          signals:  [{ probe: probe.id, level: probeLevel }],
        }, getAuthHeader());
      }
    } catch {}
    setHistory(h => [...h, { correct: pendingQuality >= 3 }]);
    setSubmitting(false);
    setTimeout(() => {
      setIndex(i => i + 1);
      setFlipped(false);
      setPhase("rate");
      setPendingQuality(null);
      startMs.current = Date.now();
    }, 300);
  };

  if (done) {
    const correct = history.filter(h => h.correct).length;
    const score   = cards.length > 0 ? Math.round((correct / cards.length) * 100) : 0;
    if (showNotes) return (
      <SessionNotesScreen sessionId={sessionId} getAuthHeader={getAuthHeader}
        startMs={sessionStart.current} onDone={onDone} childName={childName} score={score} />
    );
    return (
      <CelebrationScreen
        correct={correct} total={cards.length} childName={childName}
        onReplay={() => {
          closedRef.current    = false;
          sessionStart.current = Date.now();
          setIndex(0); setFlipped(false); setHistory([]);
          setPhase("rate"); setPendingQuality(null); setShowNotes(false);
        }}
        onExit={() => setShowNotes(true)}
      />
    );
  }

  const progress = (index / cards.length) * 100;

  return (
    <div style={{ minHeight:"100vh", background:"#f0f4ff", display:"flex", flexDirection:"column" }}>
      <div style={{ background:"white", borderBottom:"1px solid #dce3f5", padding:"10px 20px",
                    display:"flex", alignItems:"center", gap:12 }}>
        <button onClick={onDone}
          style={{ background:"#f0f4ff", border:"none", borderRadius:8, padding:"7px 14px",
                   cursor:"pointer", fontSize:12, color:"#666", fontWeight:700 }}>← Exit</button>
        <div style={{ flex:1 }}>
          <div style={{ fontWeight:700, fontSize:13, color:"#003366" }}>🃏 Flip Cards</div>
          {childName && <div style={{ fontSize:11, color:"#888" }}>{childName}</div>}
        </div>
        <div style={{ fontSize:12, color:"#888", fontWeight:600 }}>{index + 1} / {cards.length}</div>
      </div>

      <div style={{ height:5, background:"#dce3f5" }}>
        <div style={{ width:`${progress}%`, height:"100%", background:"#003366",
                      transition:"width .4s" }} />
      </div>

      <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center",
                    justifyContent:"center", padding:24 }}>

        <div onClick={phase === "rate" ? revealCard : undefined}
          style={{ width:"min(440px,90vw)", background:"white", borderRadius:24,
                   border:"3px solid #003366", overflow:"hidden",
                   boxShadow:"0 12px 48px rgba(0,51,102,.18)",
                   cursor: phase === "rate" && !flipped ? "pointer" : "default",
                   userSelect:"none" }}>
          <div style={{ height:"min(340px,48vw)", display:"flex", alignItems:"center",
                        justifyContent:"center", background:"#f8f9ff", padding:20 }}>
            <img src={card.imageUrl} alt={label}
              style={{ maxWidth:"100%", maxHeight:"100%", objectFit:"contain" }} />
          </div>
          <div style={{ padding:"18px 20px", borderTop:"2px solid #e0e7f0", textAlign:"center",
                        minHeight:72, display:"flex", alignItems:"center", justifyContent:"center",
                        flexDirection:"column", gap:8 }}>
            {flipped ? (
              <>
                <div style={{ fontSize:26, fontWeight:800, color:"#003366", lineHeight:1.2 }}>
                  {label}
                </div>
                <button onClick={() => speak(label, language)}
                  style={{ background:"#e8f0fe", border:"none", borderRadius:20, padding:"5px 14px",
                           fontSize:12, color:"#003366", fontWeight:700, cursor:"pointer" }}>
                  🔊 Hear again
                </button>
              </>
            ) : (
              <div style={{ fontSize:15, color:"#aaa", fontWeight:600 }}>👆 Tap to reveal</div>
            )}
          </div>
        </div>

        {/* Phase A — quality rating */}
        {flipped && phase === "rate" && (
          <div style={{ marginTop:24, width:"min(440px,90vw)" }}>
            <div style={{ fontSize:11, fontWeight:700, color:"#888", textAlign:"center",
                          textTransform:"uppercase", letterSpacing:0.8, marginBottom:12 }}>
              {readOnly ? "How did your child respond?" : "How did the child respond?"}
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10 }}>
              {[
                { q:1, label:"Needs Help",  emoji:"🔴", color:"#C0392B", bg:"#FDEDEC" },
                { q:3, label:"With Prompt", emoji:"🟡", color:"#D4AC0D", bg:"#FEF9E7" },
                { q:5, label:"Got It! ✓",   emoji:"🟢", color:"#1B6B45", bg:"#EDF7F2" },
              ].map(r => (
                <button key={r.q} onClick={() => handleQualityRate(r.q)}
                  style={{ padding:"18px 8px", background:r.bg, border:`2.5px solid ${r.color}`,
                           borderRadius:16, cursor:"pointer", fontWeight:800, fontSize:13,
                           color:r.color, display:"flex", flexDirection:"column",
                           alignItems:"center", gap:6, minHeight:72 }}>
                  <span style={{ fontSize:28 }}>{r.emoji}</span>
                  {r.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Phase B — probe */}
        {flipped && phase === "probe" && (
          <ProbeRound
            probe={probe}
            label={label}
            category={card.category}
            onRate={handleProbeRate}
            submitting={submitting}
          />
        )}
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MODE 2 — MULTIPLE CHOICE QUIZ  (with probe after each answer)
// ══════════════════════════════════════════════════════════════════════════════
//
// Flow per question:
//   1. Question shown → user picks answer → outcome shown
//   2. 600ms delay → probe question shown (same card still visible)
//   3. Teacher rates probe → review posted with signals[], advance to next Q
//
function cardKey(c) {
  return c?.imageUrl || c?.cardId || c?.id || "";
}

function QuizMode({ cards, language, sessionId, getAuthHeader, childName, onDone }) {
  const buildRound = useCallback((shuffled) => {
    return shuffled.map((card, idx) => {
      const pool        = shuffled.filter((_, i) => i !== idx);
      const distractors = shuffle(pool).slice(0, 3);
      return { card, options: shuffle([card, ...distractors]) };
    });
  }, []);

  const rounds       = useRef(buildRound(shuffle(cards)));
  const sessionStart = useRef(Date.now());
  const closedRef    = useRef(false);
  const startMs      = useRef(Date.now());
  // Stores the data needed to POST the review — held until probe is rated
  const pendingReview = useRef(null);

  const [showNotes,   setShowNotes]   = useState(false);
  const [index,       setIndex]       = useState(0);
  const [selectedKey, setSelectedKey] = useState(null);
  const [outcome,     setOutcome]     = useState(null);
  const [history,     setHistory]     = useState([]);
  const [submitting,  setSubmitting]  = useState(false);
  const [showProbe,   setShowProbe]   = useState(false); // show probe after outcome delay

  const round = rounds.current[index];
  const done  = index >= rounds.current.length;
  const probe = PROBES[index % PROBES.length];

  useEffect(() => {
    if (done && !closedRef.current) {
      closedRef.current = true;
      closeSession(sessionId, getAuthHeader, sessionStart.current);
    }
  }, [done]);

  const choose = (option) => {
    if (selectedKey || submitting) return;

    const correctKey = cardKey(round.card);
    const chosenKey  = cardKey(option);
    const isCorrect  = chosenKey === correctKey;

    setSelectedKey(chosenKey);
    setOutcome(isCorrect ? "correct" : "wrong");

    const label = round.card.labels?.[language] || round.card.labels?.en || "";
    speak(isCorrect ? label : "Try again", language);

    // Store review data — will be posted when probe is rated
    pendingReview.current = {
      cardId:     round.card.cardId || round.card.id,
      quality:    isCorrect ? 5 : 1,
      responseMs: Date.now() - startMs.current,
      prompted:   false,
    };

    setHistory(h => [...h, { correct: isCorrect }]);

    // After a short pause to show outcome, show probe
    setTimeout(() => setShowProbe(true), 700);
  };

  const handleProbeRate = async (probeLevel) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      if (sessionId && pendingReview.current) {
        await apiFetch("PATCH", `/flashcard-sessions/${sessionId}/review`, {
          ...pendingReview.current,
          signals: [{ probe: probe.id, level: probeLevel }],
        }, getAuthHeader());
      }
    } catch {}
    pendingReview.current = null;
    setSubmitting(false);
    // Advance
    setTimeout(() => {
      setIndex(i => i + 1);
      setSelectedKey(null);
      setOutcome(null);
      setShowProbe(false);
      startMs.current = Date.now();
    }, 200);
  };

  if (done) {
    const correct = history.filter(h => h.correct).length;
    const score   = cards.length > 0 ? Math.round((correct / cards.length) * 100) : 0;
    if (showNotes) return (
      <SessionNotesScreen sessionId={sessionId} getAuthHeader={getAuthHeader}
        startMs={sessionStart.current} onDone={onDone} childName={childName} score={score} />
    );
    return (
      <CelebrationScreen
        correct={correct} total={cards.length} childName={childName}
        onReplay={() => {
          closedRef.current    = false;
          sessionStart.current = Date.now();
          rounds.current = buildRound(shuffle(cards));
          setIndex(0); setHistory([]); setShowNotes(false);
          setShowProbe(false); setSelectedKey(null); setOutcome(null);
        }}
        onExit={() => setShowNotes(true)}
      />
    );
  }

  const targetLabel = round.card.labels?.[language] || round.card.labels?.en || round.card.cardLabel || "";
  const correctKey  = cardKey(round.card);
  const progress    = (index / rounds.current.length) * 100;

  return (
    <div style={{ minHeight:"100vh", background:"#f0f4ff", display:"flex", flexDirection:"column" }}>
      <div style={{ background:"white", borderBottom:"1px solid #dce3f5", padding:"10px 20px",
                    display:"flex", alignItems:"center", gap:12 }}>
        <button onClick={onDone}
          style={{ background:"#f0f4ff", border:"none", borderRadius:8, padding:"7px 14px",
                   cursor:"pointer", fontSize:12, color:"#666", fontWeight:700 }}>← Exit</button>
        <div style={{ flex:1 }}>
          <div style={{ fontWeight:700, fontSize:13, color:"#003366" }}>🎯 Multiple Choice</div>
          {childName && <div style={{ fontSize:11, color:"#888" }}>{childName}</div>}
        </div>
        <div style={{ fontSize:12, color:"#888", fontWeight:600 }}>
          Q {index + 1} / {rounds.current.length}
        </div>
      </div>

      <div style={{ height:5, background:"#dce3f5" }}>
        <div style={{ width:`${progress}%`, height:"100%", background:"#003366",
                      transition:"width .4s" }} />
      </div>

      <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center",
                    justifyContent:"center", padding:"20px 16px" }}>

        {!showProbe ? (
          <>
            <div style={{ fontSize:15, fontWeight:700, color:"#555", marginBottom:20,
                          textAlign:"center" }}>
              Which one is
              <span style={{ color:"#003366", fontWeight:900, fontSize:20 }}> "{targetLabel}"</span> ?
              <button onClick={() => speak(targetLabel, language)}
                style={{ marginLeft:10, background:"#e8f0fe", border:"none", borderRadius:20,
                         padding:"4px 10px", fontSize:11, color:"#003366", fontWeight:700,
                         cursor:"pointer", verticalAlign:"middle" }}>🔊</button>
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12,
                          width:"min(520px,94vw)" }}>
              {round.options.map((opt, i) => {
                const optKey    = cardKey(opt);
                const isChosen  = selectedKey && optKey === selectedKey;
                const isCorrect = optKey === correctKey;

                let border = "2.5px solid #dce3f5", bg = "white";
                if (selectedKey) {
                  if (isCorrect)     { border = "2.5px solid #1B6B45"; bg = "#EDF7F2"; }
                  else if (isChosen) { border = "2.5px solid #C0392B"; bg = "#FDEDEC"; }
                }

                return (
                  <button key={i} onClick={() => choose(opt)} disabled={!!selectedKey}
                    style={{ background:bg, border, borderRadius:16, overflow:"hidden",
                             cursor:selectedKey ? "default" : "pointer", textAlign:"center",
                             boxShadow:"0 2px 8px rgba(0,0,0,.07)", transition:"all .2s",
                             display:"flex", flexDirection:"column", alignItems:"center",
                             padding:0, position:"relative" }}>
                    {selectedKey && isCorrect && (
                      <div style={{ position:"absolute", top:8, right:8, background:"#1B6B45",
                                    color:"white", borderRadius:"50%", width:26, height:26,
                                    display:"flex", alignItems:"center", justifyContent:"center",
                                    fontSize:14, fontWeight:900, zIndex:2 }}>✓</div>
                    )}
                    {selectedKey && isChosen && !isCorrect && (
                      <div style={{ position:"absolute", top:8, right:8, background:"#C0392B",
                                    color:"white", borderRadius:"50%", width:26, height:26,
                                    display:"flex", alignItems:"center", justifyContent:"center",
                                    fontSize:14, fontWeight:900, zIndex:2 }}>✗</div>
                    )}
                    <div style={{ width:"100%", height:130, display:"flex", alignItems:"center",
                                  justifyContent:"center", background:"#f8f9ff",
                                  padding:12, overflow:"hidden" }}>
                      <img src={opt.imageUrl} alt=""
                        style={{ maxWidth:"100%", maxHeight:"100%", objectFit:"contain" }} />
                    </div>
                  </button>
                );
              })}
            </div>

            {outcome && (
              <div style={{ marginTop:20, fontSize:18, fontWeight:800,
                            color: outcome === "correct" ? "#1B6B45" : "#C0392B",
                            animation:"fadeUp .25s ease" }}>
                {outcome === "correct" ? "✅ Correct! Well done!" : "❌ Not quite — let's try next one"}
              </div>
            )}
          </>
        ) : (
          /* Probe phase — show the correct card + probe question */
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center" }}>
            <div style={{ width:"min(200px,44vw)", background:"white", borderRadius:16,
                          border:"2.5px solid #1B6B45", overflow:"hidden",
                          boxShadow:"0 6px 24px rgba(0,51,102,.12)", marginBottom:4 }}>
              <div style={{ height:140, display:"flex", alignItems:"center",
                            justifyContent:"center", background:"#EDF7F2", padding:12 }}>
                <img src={round.card.imageUrl} alt={targetLabel}
                  style={{ maxWidth:"100%", maxHeight:"100%", objectFit:"contain" }} />
              </div>
              <div style={{ padding:"10px", textAlign:"center", fontWeight:800,
                            fontSize:14, color:"#003366", borderTop:"1px solid #e0e7f0" }}>
                {targetLabel}
              </div>
            </div>
            <ProbeRound
              probe={probe}
              label={targetLabel}
              category={round.card.category}
              onRate={handleProbeRate}
              submitting={submitting}
            />
          </div>
        )}
      </div>
      <style>{`
        @keyframes fadeUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spin   { to{transform:rotate(360deg)} }
      `}</style>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MODE 3 — MATCH PAIRS  (with probe after each successful match)
// ══════════════════════════════════════════════════════════════════════════════
//
// Flow per match:
//   1. Child flips tiles → match detected → tiles locked to matched state
//   2. Probe panel slides in for the matched card
//   3. Teacher rates probe → review posted with signals[] → panel clears,
//      game continues
//
function MatchMode({ cards, language, sessionId, getAuthHeader, childName, onDone }) {
  const gameCards     = useRef(shuffle(cards).slice(0, 6));
  const sessionStart  = useRef(Date.now());
  const closedRef     = useRef(false);
  const submittingRef = useRef(false);
  const matchCountRef = useRef(0); // used by probe to cycle PROBES correctly

  const [showNotes,  setShowNotes]  = useState(false);
  const [probeCard,  setProbeCard]  = useState(null); // { cardId, label, imageUrl, category }
  const [probeSubmitting, setProbeSubmitting] = useState(false);

  function buildTiles() {
    const pairs = gameCards.current.map(c => {
      const label = c.labels?.[language] || c.labels?.en || c.cardLabel || "";
      const id    = c.cardId || c.id;
      return [
        { key:`img-${id}`, type:"image", cardId:id, imageUrl:c.imageUrl, label, category:c.category||"Custom", matched:false, flipped:false },
        { key:`lbl-${id}`, type:"label", cardId:id, imageUrl:c.imageUrl, label, category:c.category||"Custom", matched:false, flipped:false },
      ];
    }).flat();
    return shuffle(pairs);
  }

  const [tiles,      setTiles]      = useState(buildTiles);
  const [selected,   setSelected]   = useState([]);
  const [matchCount, setMatchCount] = useState(0);
  const [history,    setHistory]    = useState([]);
  const [locking,    setLocking]    = useState(false);

  const totalPairs = gameCards.current.length;
  const done       = matchCount >= totalPairs && !probeCard;

  useEffect(() => {
    if (matchCount >= totalPairs && !probeCard && !closedRef.current) {
      closedRef.current = true;
      closeSession(sessionId, getAuthHeader, sessionStart.current);
    }
  }, [matchCount, probeCard]);

  const flipTile = (key) => {
    if (locking || probeCard) return;
    const tile = tiles.find(t => t.key === key);
    if (!tile || tile.matched || tile.flipped) return;
    if (selected.length === 1 && selected[0] === key) return;

    const next = [...selected, key];
    setTiles(p => p.map(t => t.key === key ? { ...t, flipped:true } : t));
    setSelected(next);

    if (next.length === 2) {
      setLocking(true);
      const [k1, k2] = next;
      const t1 = tiles.find(t => t.key === k1);
      const t2 = tiles.find(t => t.key === key);
      const isMatch = t1.cardId === t2.cardId && t1.type !== t2.type;

      speak(t2.label, language);

      setTimeout(() => {
        if (isMatch) {
          setTiles(p => p.map(t =>
            (t.key === k1 || t.key === k2) ? { ...t, matched:true, flipped:true } : t
          ));
          setMatchCount(c => c + 1);
          matchCountRef.current += 1;
          setHistory(h => [...h, { correct:true, cardId:t1.cardId }]);
          // Show probe for the matched card
          setProbeCard({ cardId: t1.cardId, label: t1.label, imageUrl: t1.imageUrl, category: t1.category });
        } else {
          setTiles(p => p.map(t =>
            (t.key === k1 || t.key === k2) ? { ...t, flipped:false } : t
          ));
          setHistory(h => [...h, { correct:false }]);
        }
        setSelected([]);
        setLocking(false);
      }, isMatch ? 500 : 1000);
    }
  };

  const handleProbeRate = async (probeLevel) => {
    if (probeSubmitting || !probeCard) return;
    setProbeSubmitting(true);
    // Probe cycles by number of matches made so far
    const probeIdx = (matchCountRef.current - 1) % PROBES.length;
    const probe    = PROBES[probeIdx];
    try {
      if (sessionId && !submittingRef.current) {
        submittingRef.current = true;
        await apiFetch("PATCH", `/flashcard-sessions/${sessionId}/review`, {
          cardId:  probeCard.cardId,
          quality: probeLevel === "strong" ? 5 : probeLevel === "emerging" ? 3 : 1,
          responseMs: null,
          prompted: false,
          signals: [{ probe: probe.id, level: probeLevel }],
        }, getAuthHeader());
        submittingRef.current = false;
      }
    } catch {}
    setProbeSubmitting(false);
    setProbeCard(null);
  };

  const replay = () => {
    closedRef.current    = false;
    sessionStart.current = Date.now();
    matchCountRef.current = 0;
    setTiles(buildTiles());
    setSelected([]);
    setMatchCount(0);
    setHistory([]);
    setLocking(false);
    setProbeCard(null);
    setShowNotes(false);
  };

  if (done) {
    const correct = history.filter(h => h.correct).length;
    const total   = history.length;
    const score   = total > 0 ? Math.round((correct / total) * 100) : 100;
    if (showNotes) return (
      <SessionNotesScreen sessionId={sessionId} getAuthHeader={getAuthHeader}
        startMs={sessionStart.current} onDone={onDone} childName={childName} score={score} />
    );
    return (
      <CelebrationScreen correct={correct} total={total}
        childName={childName}
        onReplay={replay} onExit={() => setShowNotes(true)} />
    );
  }

  // Which probe shows for the CURRENT match (index = matchCount before this match = matchCount-1)
  const currentProbeIdx = Math.max(0, matchCountRef.current - 1) % PROBES.length;
  const currentProbe    = PROBES[currentProbeIdx];

  return (
    <div style={{ minHeight:"100vh", background:"#f0f4ff", display:"flex", flexDirection:"column" }}>
      <div style={{ background:"white", borderBottom:"1px solid #dce3f5", padding:"10px 20px",
                    display:"flex", alignItems:"center", gap:12 }}>
        <button onClick={onDone}
          style={{ background:"#f0f4ff", border:"none", borderRadius:8, padding:"7px 14px",
                   cursor:"pointer", fontSize:12, color:"#666", fontWeight:700 }}>← Exit</button>
        <div style={{ flex:1 }}>
          <div style={{ fontWeight:700, fontSize:13, color:"#003366" }}>🧩 Match Pairs</div>
          {childName && <div style={{ fontSize:11, color:"#888" }}>{childName}</div>}
        </div>
        <div style={{ fontSize:12, fontWeight:700, color:"#1B6B45" }}>
          {matchCount} / {totalPairs} matched
        </div>
      </div>

      <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center",
                    justifyContent:"center", padding:"20px 12px" }}>

        {/* Tile grid — dimmed when probe is showing */}
        <div style={{ opacity: probeCard ? 0.35 : 1, transition:"opacity .25s",
                      pointerEvents: probeCard ? "none" : "auto" }}>
          <div style={{ fontSize:13, color:"#888", marginBottom:16, fontWeight:600,
                        textAlign:"center" }}>
            Match each image to its name
          </div>
          <div style={{ display:"grid",
                        gridTemplateColumns:`repeat(${Math.min(tiles.length, 4)},1fr)`,
                        gap:10, width:"min(600px,96vw)" }}>
            {tiles.map(tile => {
              const isUp  = tile.flipped || tile.matched;
              const isSel = selected.includes(tile.key);
              let bg     = "white",
                  border = "2.5px solid #dce3f5",
                  shadow = "0 2px 6px rgba(0,0,0,.08)";
              if (tile.matched)  { bg = "#EDF7F2"; border = "2.5px solid #1B6B45"; }
              else if (isSel)    { bg = "#EBF4FB"; border = "2.5px solid #003366"; shadow = "0 4px 16px rgba(0,51,102,.2)"; }

              return (
                <div key={tile.key}
                  onClick={() => !tile.matched && flipTile(tile.key)}
                  style={{ background:bg, border, borderRadius:14, overflow:"hidden",
                           cursor:tile.matched ? "default" : "pointer", transition:"all .2s",
                           boxShadow:shadow, minHeight:90,
                           display:"flex", alignItems:"center", justifyContent:"center" }}>
                  {!isUp ? (
                    <div style={{ width:"100%", height:"100%", minHeight:90, display:"flex",
                                  alignItems:"center", justifyContent:"center",
                                  background:"linear-gradient(135deg,#003366,#1a5c99)",
                                  fontSize:22, color:"white", fontWeight:900, letterSpacing:1 }}>
                      NISH
                    </div>
                  ) : tile.type === "image" ? (
                    <div style={{ width:"100%", height:"100%", minHeight:90, padding:8,
                                  display:"flex", alignItems:"center", justifyContent:"center",
                                  background:tile.matched ? "#EDF7F2" : "#f8f9ff" }}>
                      <img src={tile.imageUrl} alt={tile.label}
                        style={{ maxWidth:"100%", maxHeight:80, objectFit:"contain" }} />
                    </div>
                  ) : (
                    <div style={{ padding:"12px 8px", textAlign:"center", fontWeight:800,
                                  fontSize:14, color:tile.matched ? "#1B6B45" : "#003366",
                                  lineHeight:1.3 }}>
                      {tile.label}
                      {tile.matched && <div style={{ fontSize:18, marginTop:4 }}>✓</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div style={{ marginTop:16, fontSize:11, color:"#aaa", fontWeight:600, textAlign:"center" }}>
            {history.length} attempt{history.length !== 1 ? "s" : ""} · {history.filter(h => h.correct).length} matches found
          </div>
        </div>

        {/* Probe panel — slides in after each match */}
        {probeCard && (
          <div style={{ position:"fixed", bottom:0, left:0, right:0,
                        background:"white", borderTop:"2.5px solid #003366",
                        borderRadius:"20px 20px 0 0", padding:"20px 24px 32px",
                        boxShadow:"0 -8px 40px rgba(0,51,102,.18)",
                        display:"flex", flexDirection:"column", alignItems:"center",
                        animation:"slideUp .28s cubic-bezier(.22,.68,0,1.2) both",
                        zIndex:50 }}>
            {/* Mini matched card preview */}
            <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:12,
                          width:"min(400px,88vw)" }}>
              <div style={{ width:56, height:56, borderRadius:12, overflow:"hidden",
                            border:"2px solid #1B6B45", flexShrink:0,
                            background:"#EDF7F2", display:"flex", alignItems:"center",
                            justifyContent:"center" }}>
                <img src={probeCard.imageUrl} alt={probeCard.label}
                  style={{ maxWidth:"100%", maxHeight:"100%", objectFit:"contain" }} />
              </div>
              <div>
                <div style={{ fontSize:10, fontWeight:700, color:"#1B6B45",
                              textTransform:"uppercase", letterSpacing:.6 }}>✓ Matched!</div>
                <div style={{ fontWeight:800, fontSize:16, color:"#003366" }}>{probeCard.label}</div>
              </div>
            </div>
            <div style={{ width:"min(400px,88vw)" }}>
              <ProbeRound
                probe={currentProbe}
                label={probeCard.label}
                category={probeCard.category}
                onRate={handleProbeRate}
                submitting={probeSubmitting}
              />
            </div>
          </div>
        )}
      </div>
      <style>{`@keyframes slideUp{from{transform:translateY(100%);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SETUP SCREEN  (unchanged from original)
// ══════════════════════════════════════════════════════════════════════════════
function SetupScreen({ onStart, getAuthHeader, userRole, linkedId, linkedChildId }) {
  const [children, setChildren] = useState([]);
  const [sets,     setSets]     = useState([]);
  const [childId,  setChildId]  = useState(linkedChildId || "");
  const [setId,    setSetId]    = useState("");
  const [language, setLanguage] = useState("en");
  const [mode,     setMode]     = useState("flip");
  const [loading,  setLoading]  = useState(true);
  const [starting, setStarting] = useState(false);
  const [err,      setErr]      = useState("");

  const readOnly = userRole === "parent";

  useEffect(() => {
    (async () => {
      try {
        const cd = await apiFetch("GET", "/children?limit=200", null, getAuthHeader());
        const kids = Array.isArray(cd?.children) ? cd.children
                   : Array.isArray(cd)            ? cd : [];
        setChildren(kids);

        let resolvedChildId = linkedChildId || "";
        if (!resolvedChildId) {
          if (readOnly && kids.length > 0) resolvedChildId = kids[0]._id;
          else if (kids.length === 1)      resolvedChildId = kids[0]._id;
        }
        if (resolvedChildId) setChildId(resolvedChildId);

        const setsPath = readOnly
          ? (resolvedChildId ? `/flashcard?assignedTo=${resolvedChildId}` : null)
          : "/flashcard?limit=100";

        const sd = setsPath
          ? await apiFetch("GET", setsPath, null, getAuthHeader())
          : { flashcards: [] };
        setSets(sd?.flashcards || []);

        if (linkedId) setSetId(linkedId);
      } catch (e) { setErr(e.message); }
      setLoading(false);
    })();
  }, []);

  const selectedSet   = sets.find(s => s._id === setId);
  const selectedChild = children.find(c => c._id === childId);
  const cardCount     = selectedSet?.cards?.length || 0;
  const canStart      = !!(setId && childId);

  const start = async () => {
    if (!canStart) return;
    setStarting(true); setErr("");
    try {
      let sessionId = null;
      if (childId) {
        const d = await apiFetch("POST", "/flashcard-sessions", {
          childId,
          moduleType:     mode,
          flashcardSetId: setId,
          setTitle:       selectedSet?.title || "",
          conductorRole:  userRole === "parent" ? "parent" : "teacher",
          sessionType:    userRole === "parent" ? "parent-led" : "teacher-led",
          cards: (selectedSet?.cards || []).map(c => ({
            id:    c.cardId || c.id,
            label: c.cardLabel || c.labels?.en || "",
          })),
        }, getAuthHeader());
        sessionId = d.session?._id || null;
      }
      onStart({
        cards:    selectedSet?.cards || [],
        child:    selectedChild,
        language,
        mode,
        sessionId,
        setTitle: selectedSet?.title || "",
      });
    } catch (e) { setErr(e.message); }
    setStarting(false);
  };

  if (loading) return (
    <div style={{ background:"#f0f4ff", minHeight:"100vh", display:"flex",
                  alignItems:"center", justifyContent:"center" }}>
      <Spinner />
    </div>
  );

  return (
    <div style={{ background:"#f0f4ff", minHeight:"100vh", padding:"40px 16px" }}>
      <div style={{ maxWidth:540, margin:"0 auto" }}>

        <div style={{ marginBottom:28, textAlign:"center" }}>
          <div style={{ fontSize:32, marginBottom:8 }}>🎮</div>
          <div style={{ fontFamily:"'Playfair Display',serif", fontSize:26, fontWeight:900,
                        color:"#003366", marginBottom:4 }}>Learning Activity</div>
          <div style={{ fontSize:13, color:"#888" }}>
            {readOnly ? "Select a set to view" : "Set up a digital learning session"}
          </div>
        </div>

        {err && (
          <div style={{ background:"#FDEDEC", color:"#C0392B", padding:"10px 14px",
                        borderRadius:8, fontSize:12, marginBottom:16, fontWeight:600 }}>
            {err}
          </div>
        )}

        {!readOnly && (
          <div style={{ background:"white", borderRadius:14, padding:18, marginBottom:14,
                        border:"1.5px solid #e0e7f0", boxShadow:"0 1px 4px rgba(0,0,0,.05)" }}>
            <label style={{ display:"block", fontSize:10, fontWeight:700, color:"#888",
                            textTransform:"uppercase", letterSpacing:0.8, marginBottom:8 }}>
              Child *
            </label>
            <select value={childId} onChange={e => setChildId(e.target.value)}
              style={{ width:"100%", padding:"10px 12px", border:"1.5px solid #dce3f5",
                       borderRadius:9, fontSize:13, outline:"none", background:"white" }}>
              <option value="">Select a child…</option>
              {children.map(c => {
                const age = c.dob
                  ? new Date().getFullYear() - new Date(c.dob).getFullYear()
                  : null;
                return (
                  <option key={c._id} value={c._id}>
                    {c.name}{age ? ` (${age}y)` : ""}
                  </option>
                );
              })}
            </select>
          </div>
        )}

        {readOnly && selectedChild && (
          <div style={{ background:"#EBF4FB", borderRadius:12, padding:"12px 16px",
                        marginBottom:14, border:"1.5px solid #BDD7EE",
                        display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ fontSize:20 }}>👤</span>
            <div>
              <div style={{ fontSize:11, fontWeight:700, color:"#1A4A6E",
                            textTransform:"uppercase", letterSpacing:.6 }}>Session for</div>
              <div style={{ fontSize:14, fontWeight:800, color:"#003366" }}>
                {selectedChild.name}
              </div>
            </div>
          </div>
        )}

        <div style={{ background:"white", borderRadius:14, padding:18, marginBottom:14,
                      border:"1.5px solid #e0e7f0", boxShadow:"0 1px 4px rgba(0,0,0,.05)" }}>
          <label style={{ display:"block", fontSize:10, fontWeight:700, color:"#888",
                          textTransform:"uppercase", letterSpacing:0.8, marginBottom:8 }}>
            Flashcard Set *
          </label>
          {sets.length === 0 ? (
            <div style={{ fontSize:12, color:"#aaa", textAlign:"center", padding:12 }}>
              {readOnly
                ? "No flashcard sets have been assigned yet. Ask your child's teacher."
                : "No saved sets found. Create one in the Flashcard Generator."}
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:8,
                          maxHeight:220, overflowY:"auto" }}>
              {sets.map(s => (
                <div key={s._id} onClick={() => setSetId(s._id)}
                  style={{ padding:"10px 14px",
                           border:`2px solid ${setId === s._id ? "#003366" : "#dce3f5"}`,
                           borderRadius:9, background:setId === s._id ? "#EBF4FB" : "white",
                           cursor:"pointer", display:"flex", alignItems:"center",
                           justifyContent:"space-between" }}>
                  <div>
                    <div style={{ fontWeight:700, fontSize:13, color:"#003366" }}>{s.title}</div>
                    <div style={{ fontSize:10, color:"#888" }}>
                      {(s.cards || []).length} cards · {LANGUAGES.find(l => l.code === s.language)?.name || "—"}
                    </div>
                  </div>
                  {setId === s._id && (
                    <span style={{ color:"#003366", fontWeight:900, fontSize:16 }}>✓</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:14 }}>
          <div style={{ background:"white", borderRadius:14, padding:16, border:"1.5px solid #e0e7f0" }}>
            <label style={{ display:"block", fontSize:10, fontWeight:700, color:"#888",
                            textTransform:"uppercase", letterSpacing:0.8, marginBottom:8 }}>
              Language
            </label>
            <select value={language} onChange={e => setLanguage(e.target.value)}
              style={{ width:"100%", padding:"9px 10px", border:"1.5px solid #dce3f5",
                       borderRadius:8, fontSize:12, outline:"none", background:"white" }}>
              {LANGUAGES.map(l => (
                <option key={l.code} value={l.code}>{l.name}</option>
              ))}
            </select>
          </div>
          <div style={{ background:"white", borderRadius:14, padding:16, border:"1.5px solid #e0e7f0" }}>
            <label style={{ display:"block", fontSize:10, fontWeight:700, color:"#888",
                            textTransform:"uppercase", letterSpacing:0.8, marginBottom:8 }}>
              Activity Mode
            </label>
            <select value={mode} onChange={e => setMode(e.target.value)}
              style={{ width:"100%", padding:"9px 10px", border:"1.5px solid #dce3f5",
                       borderRadius:8, fontSize:12, outline:"none", background:"white" }}>
              {MODES.map(m => (
                <option key={m.id} value={m.id}>{m.icon} {m.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, marginBottom:20 }}>
          {MODES.map(m => (
            <div key={m.id} onClick={() => setMode(m.id)}
              style={{ padding:"12px 10px",
                       background: mode === m.id ? "#EBF4FB" : "white",
                       border:`2px solid ${mode === m.id ? "#003366" : "#dce3f5"}`,
                       borderRadius:12, cursor:"pointer", textAlign:"center", transition:"all .15s" }}>
              <div style={{ fontSize:22, marginBottom:4 }}>{m.icon}</div>
              <div style={{ fontSize:11, fontWeight:700,
                            color:mode === m.id ? "#003366" : "#555" }}>{m.label}</div>
              <div style={{ fontSize:9, color:"#aaa", marginTop:2, lineHeight:1.4 }}>{m.desc}</div>
            </div>
          ))}
        </div>

        {selectedSet && (
          <div style={{ background:"#EBF4FB", borderRadius:10, padding:"10px 14px",
                        marginBottom:16, fontSize:12, color:"#1A4A6E", fontWeight:600,
                        display:"flex", gap:8, alignItems:"center" }}>
            <span style={{ fontSize:16 }}>ℹ️</span>
            <span>
              {cardCount} cards ·{" "}
              {mode === "match" ? "Match mode uses up to 6 pairs (12 tiles)" :
               mode === "quiz"  ? "Quiz shows each card once with 3 distractors" :
                                  "Flip mode shows all cards one by one"}
              {" · Each card includes one skill probe"}
            </span>
          </div>
        )}

        <button onClick={start} disabled={!canStart || starting}
          style={{ width:"100%", padding:"15px",
                   background:canStart ? "#003366" : "#ccc",
                   color:"white", border:"none", borderRadius:12,
                   fontWeight:800, fontSize:15,
                   cursor:canStart ? "pointer" : "not-allowed",
                   boxShadow:canStart ? "0 6px 24px rgba(0,51,102,.3)" : "none" }}>
          {starting ? "Starting…" : `▶ Start ${MODES.find(m => m.id === mode)?.label || "Activity"}`}
        </button>

        {!readOnly && (
          <div style={{ textAlign:"center", fontSize:11, color:"#aaa", marginTop:12 }}>
            Child responses tracked via SM-2 + BKT · one skill probe per card
          </div>
        )}
        {readOnly && (
          <div style={{ textAlign:"center", fontSize:11, color:"#aaa", marginTop:12 }}>
            Session will be logged as home practice
          </div>
        )}
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ROOT EXPORT  (unchanged)
// ══════════════════════════════════════════════════════════════════════════════
export default function FlashcardLearning() {
  const { user, getAuthHeader } = useAuth();
  const { state: navState }     = useLocation();

  const [phase,        setPhase]    = useState("setup");
  const [activityData, setActivity] = useState(null);

  const launch = (data) => { setActivity(data); setPhase("play"); };

  const exit = async () => {
    if (navState?.workplanId && navState?.entryId) {
      try {
        await fetch(`${API}/workplan/${navState.workplanId}/entry/${navState.entryId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", ...getAuthHeader() },
          body: JSON.stringify({ status: "done" }),
        });
      } catch {}
    }
    setActivity(null);
    setPhase("setup");
  };

  if (phase === "setup") {
    return (
      <SetupScreen
        getAuthHeader={getAuthHeader}
        userRole={user?.role}
        onStart={launch}
        linkedId={navState?.linkedId}
        linkedChildId={navState?.childId}
      />
    );
  }

  if (phase === "play" && activityData) {
    const { cards, child, language, mode, sessionId } = activityData;
    const childName   = child?.name || "";
    const commonProps = { cards, language, sessionId, getAuthHeader, childName, onDone: exit };

    if (mode === "quiz")  return <QuizMode  {...commonProps} />;
    if (mode === "match") return <MatchMode {...commonProps} />;
    return <FlipMode {...commonProps} readOnly={user?.role === "parent"} />;
  }

  return null;
}